/**
 * My Inquiries on the merged People Tickets table: the signed-in employee's own requests, whatever
 * team they went to. The proxy returns only their tickets and strips the Internal Notes, so this
 * view filters nothing for privacy — it only filters by status.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@finn-auto/design-system/atoms/button';
import { useAuth } from '../../../../contexts/AuthContext';
import { LoadingSpinner } from '../../../../shared/components/LoadingSpinner';
import { fetchPeopleTickets } from '../../services/peopleTicketsApi';
import { TICKET_STATUSES } from '../../types/bpTickets';
import { CategoryBadge, PriorityBadge, StatusBadge } from '../../components/ticketing/badges';
import { MultiSelect } from '../../components/ticketing/MultiSelect';
import { OPEN_STATUSES, resolutionTimeLabel } from '../../components/peopleTickets/ticketList';

export function PeopleMyInquiries() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const tickets = useQuery({ queryKey: ['people-tickets-mine'], queryFn: fetchPeopleTickets });
  const [statusFilter, setStatusFilter] = useState<Set<string>>(new Set(OPEN_STATUSES));
  const myEmail = (user?.email || '').toLowerCase();

  // Ticket Management holders receive EVERY ticket from the same endpoint, so "mine" is narrowed
  // here too. For everyone else the proxy has already done it.
  const mine = useMemo(
    () =>
      (tickets.data ?? [])
        .filter((t) => t.submittedBy === myEmail)
        .filter((t) => statusFilter.size === 0 || statusFilter.has(t.status))
        .sort((a, b) => Date.parse(b.submittedAt ?? '') - Date.parse(a.submittedAt ?? '')),
    [tickets.data, myEmail, statusFilter],
  );

  if (tickets.isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <LoadingSpinner size="lg" aria-label="Loading your inquiries" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl p-6">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-black">My inquiries</h1>
          <p className="mt-2 body-14-regular text-iron">Signed in as {user?.email}</p>
        </div>
        <div className="flex gap-2">
          <MultiSelect
            label="Status"
            options={TICKET_STATUSES.map((s) => ({ value: s, label: s }))}
            selected={statusFilter}
            onChange={setStatusFilter}
            triggerClassName="rounded border-pearl text-black body-14-regular"
          />
          <Button size="sm" className="rounded" onClick={() => navigate('/submit-request')}>
            New request
          </Button>
        </div>
      </div>

      {tickets.isError && <p className="mb-3 body-14-regular text-red">Your inquiries could not be loaded. Please reload the page.</p>}

      {/* m-0 p-0: the app's global list styles indent every <ul> by 40px. */}
      <ul className="m-0 list-none space-y-4 p-0">
        {mine.map((t) => (
          <li key={t.id} className="rounded border border-solid border-pearl bg-white p-5">
            <div className="flex items-start justify-between gap-4">
              {/* Reference above the title, each its own block: inline, a design-system type
                  class breaks the line and indented the title. */}
              <div className="min-w-0 space-y-1.5">
                <p className="body-12-regular text-iron">{t.ticketKey}</p>
                <h2 className="body-14-semibold text-black">{t.summary}</h2>
              </div>
              <StatusBadge status={t.status} />
            </div>
            {t.description && <p className="mt-4 whitespace-pre-wrap body-14-regular text-black">{t.description}</p>}
            {t.resolutionNote && (
              <div className="mt-4 rounded bg-green/10 px-4 py-3">
                <p className="mb-1.5 body-12-regular text-iron">Resolution</p>
                <p className="whitespace-pre-wrap body-14-regular text-black">{t.resolutionNote}</p>
              </div>
            )}
            <div className="mt-5 flex flex-wrap items-center gap-2 border-t border-solid border-pearl pt-4">
              <CategoryBadge category={t.category} />
              <PriorityBadge priority={t.priority} />
              <span className="body-12-regular text-iron">
                {resolutionTimeLabel(t.businessDaysToResolve) ??
                  `Submitted ${t.submittedAt ? new Date(t.submittedAt).toLocaleDateString() : ''}`}
              </span>
              <span className="ml-auto body-12-regular text-iron">Handled by: {t.assignee?.name || 'not assigned yet'}</span>
            </div>
          </li>
        ))}
        {mine.length === 0 && !tickets.isError && <p className="body-14-regular text-iron">No inquiries match this filter.</p>}
      </ul>
    </div>
  );
}
